import {
	UseMutationResult,
	UseQueryResult,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { ObjectId } from "bson";

import { toast } from "@performyard/py-components";
import {
	CopySurveyRequestJson,
	CreateSurveyRequest,
	CreateSurveyResponseRequestJson,
	GetAutoflowByIdResponseJson,
	GetSurveyLinkResponseJson,
	GetSurveyParticipantResponseResponseJson,
	GetSurveyParticipantsQuery,
	GetSurveyParticipantsResponseJson,
	ReScheduleSurveyEmailRequestJson,
	ScheduleSurveyEmailRequestJson,
	SurveyModelJson,
	SurveyParticipantJson,
	SurveyQuestionsResultsResponse,
	SurveyResultsCSVResponse,
	SurveyResultsOverviewResponse,
	SurveyResultsRequestJson,
	UpdateSurveyRequestJson,
} from "@performyard/service-contracts";

import { formatErrorMessage } from "../../../utils/TanstackQueryErrorHandling";

import {
	copySurvey,
	createSurvey,
	createSurveyResponse,
	deleteSurvey,
	downloadSurveyResultsCSV,
	getParticipantsEligibleForEmail,
	getSurvey,
	getSurveyByLinkId,
	getSurveyParticipantResponse,
	getSurveyParticipants,
	getSurveyQuestionsResults,
	getSurveyRespondents,
	getSurveyResultsOverview,
	getSurveyScheduleEmailEventsByResourceId,
	reScheduleSurveyEmail,
	scheduleSurveyEmail,
	updateSurvey,
} from "../../services/defs/internal-api";
import { PYUseMutationOptions, PYUseQueryOptions } from "../StandardQueryArguments";

export const useCreateSurvey = (options: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async (data: CreateSurveyRequest) => await createSurvey(data),
		meta: {
			disableDefaultErrorHandling: true,
		},
		...options,
	});
};

export const useCopySurvey = (options: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async (data: CopySurveyRequestJson) => await copySurvey(data),
		meta: {
			disableDefaultErrorHandling: true,
		},
		...options,
	});
};

export const useDeleteSurvey = (options: PYUseMutationOptions) => {
	return useMutation({
		mutationFn: async (surveyId: string) => await deleteSurvey(surveyId),
		...options,
	});
};

export const useGetSurvey = (
	surveyId: string,
	options?: PYUseQueryOptions<SurveyModelJson>
): UseQueryResult<SurveyModelJson> => {
	return useQuery({
		queryKey: ["survey", surveyId],
		queryFn: async () => await getSurvey(surveyId),
		...options,
	});
};

export const useGetSurveyResultsOverview = (
	surveyId: string,
	options?: PYUseQueryOptions<SurveyResultsOverviewResponse>
): UseQueryResult<SurveyResultsOverviewResponse> => {
	return useQuery({
		queryKey: ["surveyResultsOverview", surveyId],
		queryFn: async () => await getSurveyResultsOverview(surveyId),
		...options,
	});
};

export const useGetSurveyQuestionsResults = (
	surveyId: string,
	request: SurveyResultsRequestJson,
	options?: PYUseQueryOptions<SurveyQuestionsResultsResponse>
): UseQueryResult<SurveyQuestionsResultsResponse> => {
	return useQuery({
		queryKey: ["surveyResults", surveyId, JSON.stringify(request)],
		queryFn: async () => await getSurveyQuestionsResults(surveyId, request),
		...options,
	});
};

export const useUpdateSurvey = (
	surveyId: string,
	options?: PYUseMutationOptions
): UseMutationResult<
	SurveyModelJson,
	Error,
	Omit<UpdateSurveyRequestJson, "startDate" | "endDate"> & { startDate?: Date; endDate?: Date | null }
> => {
	const { onSuccess } = options ?? {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (
			data: Omit<UpdateSurveyRequestJson, "startDate" | "endDate"> & { startDate?: Date; endDate?: Date | null }
		) => await updateSurvey(surveyId, data),
		meta: {
			disableDefaultErrorHandling: true,
		},
		...options,
		onSuccess: (data, variables, onMutate, context) => {
			queryClient.setQueryData(["survey", surveyId], data);
			queryClient.invalidateQueries({ queryKey: ["search-service"] });
			onSuccess?.(data, variables, onMutate, context);
		},
	});
};

export const useGetParticipantsEligibleForEmail = (
	surveyId: string,
	options?: PYUseQueryOptions<SurveyParticipantJson[]>
) => {
	return useQuery({
		queryKey: ["participantsEligibleForEmail", surveyId],
		queryFn: async () => await getParticipantsEligibleForEmail(surveyId),
		...options,
	});
};

export const useGetSurveyRespondents = (surveyId: string, options?: PYUseQueryOptions<ObjectId[]>) => {
	return useQuery({
		queryKey: ["surveyRespondents", surveyId],
		queryFn: async () => await getSurveyRespondents(surveyId),
		...options,
	});
};

export const useDownloadSurveyResultsCSV = (
	surveyId: string,
	options?: PYUseMutationOptions<SurveyResultsCSVResponse>
) => {
	return useMutation({
		mutationFn: async (data: SurveyResultsRequestJson) => await downloadSurveyResultsCSV(surveyId, data),
		...options,
	});
};

export const useGetSurveyByLinkId = (surveyLinkId: string, options?: PYUseQueryOptions<GetSurveyLinkResponseJson>) => {
	return useQuery({
		queryKey: ["surveyByLinkId", surveyLinkId],
		queryFn: async () => await getSurveyByLinkId(surveyLinkId),
		...options,
	});
};

export const useCreateSurveyResponse = (
	surveyId: string,
	surveyLinkId: string,
	options?: PYUseMutationOptions
): UseMutationResult<{ success: boolean; message?: string }, Error, CreateSurveyResponseRequestJson> => {
	const { onSuccess, onError } = options ?? {};
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: CreateSurveyResponseRequestJson) => await createSurveyResponse(surveyId, data),
		meta: {
			disableDefaultErrorHandling: true,
		},
		...options,
		onError: async (error, variables, mutation, context) => {
			const surveyLinkResponse =
				queryClient.getQueryData<GetSurveyLinkResponseJson>(["surveyByLinkId", surveyLinkId]) ?? {};
			if (error.data?.message?.includes("Survey not found")) {
				queryClient.setQueryData(["surveyByLinkId", surveyLinkId], {
					...surveyLinkResponse,
					survey: null,
					surveyAvailabilityStatus: "unavailable",
				});
			} else if (error.data?.message?.includes("Survey is not open")) {
				queryClient.setQueryData(["surveyByLinkId", surveyLinkId], {
					...surveyLinkResponse,
					survey: null,
					surveyAvailabilityStatus: "closed",
				});
			} else if (error.data?.message?.includes("Survey response already exists for this respondent")) {
				queryClient.setQueryData(["surveyByLinkId", surveyLinkId], {
					...surveyLinkResponse,
					survey: null,
					surveyAvailabilityStatus: "completed",
				});
			} else {
				//default error handling is turned off for this mutation
				const messages = formatErrorMessage(error, mutation);

				messages.forEach((message) => {
					toast.error(message);
				});
			}
			onError?.(error, variables, mutation, context);
		},
		onSuccess: (data, variables, onMutate, context) => {
			const surveyLinkResponse =
				queryClient.getQueryData<GetSurveyLinkResponseJson>(["surveyByLinkId", surveyLinkId]) ?? {};

			// manually update the survey availability status to completed, thus changing the view to the thank you page
			queryClient.setQueryData(["surveyByLinkId", surveyLinkId], {
				...surveyLinkResponse,
				surveyAvailabilityStatus: "completed",
			});

			onSuccess?.(data, variables, onMutate, context);
		},
	});
};

export const useSurveyScheduleEmailEventsByResourceId = (
	resourceId: string,
	options?: PYUseQueryOptions<{
		autoflow: GetAutoflowByIdResponseJson;
		scheduledEvents: Record<string, unknown>;
	}>
) => {
	return useQuery<{
		autoflow: GetAutoflowByIdResponseJson;
		scheduledEvents: Record<string, unknown>;
	}>({
		queryKey: ["autoflow-scheduled-events", resourceId],
		queryFn: async () => await getSurveyScheduleEmailEventsByResourceId(resourceId),
		enabled: !!resourceId,
		...options,
	});
};

export const useScheduleSurveyEmail = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: ScheduleSurveyEmailRequestJson) => await scheduleSurveyEmail(data),
		...options,
		onSuccess: (response, variables, onMutate, context) => {
			queryClient.invalidateQueries({
				queryKey: ["participantsEligibleForEmail", variables.surveyId],
			});
			queryClient.invalidateQueries({
				queryKey: ["surveyEmailSchedule", variables.surveyId],
			});
			options?.onSuccess?.(response, variables, onMutate, context);
		},
	});
};

export const useReScheduleSurveyEmail = (options?: PYUseMutationOptions) => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (data: ReScheduleSurveyEmailRequestJson) => await reScheduleSurveyEmail(data),
		...options,
		onSuccess: (response, variables, onMutate, context) => {
			queryClient.invalidateQueries({
				queryKey: ["participantsEligibleForEmail", variables.surveyId],
			});
			queryClient.invalidateQueries({
				queryKey: ["surveyEmailSchedule", variables.surveyId],
			});
			options?.onSuccess?.(response, variables, onMutate, context);
		},
	});
};

export const useGetSurveyParticipants = (
	surveyId: string,
	query: GetSurveyParticipantsQuery,
	options?: PYUseQueryOptions<GetSurveyParticipantsResponseJson>
) => {
	return useQuery({
		queryKey: ["surveyParticipants", surveyId, query],
		queryFn: async () => await getSurveyParticipants(surveyId, query),
		enabled: !!surveyId,
		...options,
	});
};

export const useGetSurveyParticipantsInfinite = (
	surveyId: string,
	query: Omit<GetSurveyParticipantsQuery, "offset"> & { limit: number }
) => {
	return useInfiniteQuery({
		queryKey: ["surveyParticipantsInfinite", surveyId, query],
		queryFn: async ({ pageParam = 0 }) => {
			return await getSurveyParticipants(surveyId, {
				...query,
				offset: pageParam,
			});
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage) => {
			const nextOffset = lastPage.offset + lastPage.limit;
			return nextOffset < lastPage.total ? nextOffset : undefined;
		},
		enabled: !!surveyId,
	});
};

export const useGetSurveyParticipantResponse = (
	surveyId: string,
	employeeId: string,
	options?: PYUseQueryOptions<GetSurveyParticipantResponseResponseJson>
) => {
	return useQuery({
		queryKey: ["surveyParticipantResponse", surveyId, employeeId],
		queryFn: async () => await getSurveyParticipantResponse(surveyId, employeeId),
		enabled: !!surveyId && !!employeeId,
		...options,
	});
};
